import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, Trash2, PlusCircle, Lock, Globe, X, ChevronLeft, Loader2 } from "lucide-react";
import {
  getEmailSnippets,
  saveEmailSnippet,
  deleteEmailSnippet,
  type EmailSnippetView,
  type EmailSnippetForm,
} from "../../api/EmailSnippetApi";
import { getProjects } from "../../api/ProjectApi";
import { Modal } from "../../components/ui";
import Editor from "../../components/Editor";
import EmailVariablePicker from "./EmailVariablePicker";

// ─── Types ────────────────────────────────────────────────────────────────────

type View = "list" | "create";

interface Props {
  workspaceId: string;
  show: boolean;
  onClose: () => void;
  /** Pre-filled values when opening from "Create snippet from draft" */
  prefill?: { subject?: string; description?: string };
}

// ─── Main Modal ────────────────────────────────────────────────────────────────

const SnippetManagerModal = ({ workspaceId, show, onClose, prefill }: Props) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [view, setView] = useState<View>(prefill ? "create" : "list");
  const [editTarget, setEditTarget] = useState<EmailSnippetView | null>(null);

  const handleCreated = () => {
    qc.invalidateQueries({ queryKey: ["email-snippets"] });
    setView("list");
    setEditTarget(null);
  };

  const handleClose = () => {
    setView("list");
    setEditTarget(null);
    onClose();
  };

  // Reset view when modal (re-)opens
  useEffect(() => {
    if (show) {
      setView(prefill ? "create" : "list");
      setEditTarget(null);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      isOpen={show}
      title={
        view === "list"
          ? t("Manage Snippets")
          : editTarget
            ? t("Edit Snippet")
            : t("Create Snippet")
      }
      onClose={handleClose}
      size="md"
    >
      <div className="px-6 py-4">
        {view === "list" ? (
          <SnippetList
            workspaceId={workspaceId}
            onEdit={(s) => {
              setEditTarget(s);
              setView("create");
            }}
            onCreate={() => {
              setEditTarget(null);
              setView("create");
            }}
          />
        ) : (
          <SnippetForm
            workspaceId={workspaceId}
            initial={editTarget ?? undefined}
            prefill={!editTarget ? prefill : undefined}
            onSaved={handleCreated}
            onCancel={() => {
              setView("list");
              setEditTarget(null);
            }}
          />
        )}
      </div>
    </Modal>
  );
};

// ─── List View ────────────────────────────────────────────────────────────────

export const SnippetList = ({
  workspaceId,
  onEdit,
  onCreate,
}: {
  workspaceId: string;
  onEdit: (s: EmailSnippetView) => void;
  onCreate: () => void;
}) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const { data: snippets = [], isLoading } = useQuery<EmailSnippetView[]>({
    queryKey: ["email-snippets"],
    queryFn: getEmailSnippets,
    staleTime: 0,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEmailSnippet(workspaceId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-snippets"] });
      setConfirmId(null);
    },
  });

  const filtered = snippets.filter((s) =>
    (s.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const { data: projects = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["projects-map"],
    queryFn: () =>
      getProjects().then((list: any[]) => list.map((p) => ({ id: p.id, name: p.name }))),
    staleTime: 60_000 * 10,
  });

  const projectMap = projects.reduce(
    (acc, p) => {
      acc[p.id] = p.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const personal = filtered.filter((s) => s.visibility === "Private");

  const pipelineGroups = filtered
    .filter((s) => s.visibility === "Shared")
    .reduce(
      (acc, s) => {
        const key = s.projectId || "__shared__";
        if (!acc[key]) acc[key] = [];
        acc[key].push(s);
        return acc;
      },
      {} as Record<string, EmailSnippetView[]>
    );

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="flex items-center gap-2 bg-gray-100 rounded-md px-3 py-2">
        <Search size={14} className="text-gray-400 shrink-0" />
        <input
          className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400"
          placeholder={t("Search snippets...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto -mx-1 divide-y divide-gray-50">
        {isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">{t("No snippets found.")}</div>
        ) : (
          <div className="flex flex-col gap-2 py-1">
            {personal.length > 0 && (
              <div>
                <div className="px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  {t("Personal")}
                </div>
                <div className="divide-y divide-gray-50 flex flex-col pt-0.5">
                  {personal.map((s) => (
                    <SnippetRow
                      key={s.id}
                      snippet={s}
                      onEdit={onEdit}
                      confirmId={confirmId}
                      setConfirmId={setConfirmId}
                      deleteMut={deleteMut}
                    />
                  ))}
                </div>
              </div>
            )}
            {Object.entries(pipelineGroups).map(([projectId, items]) => (
              <div key={projectId}>
                <div className="px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-2">
                  {projectMap[projectId] || t("Shared")}
                </div>
                <div className="divide-y divide-gray-50 flex flex-col pt-0.5">
                  {items.map((s) => (
                    <SnippetRow
                      key={s.id}
                      snippet={s}
                      onEdit={onEdit}
                      confirmId={confirmId}
                      setConfirmId={setConfirmId}
                      deleteMut={deleteMut}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-2 border-t border-gray-100">
        <button
          onClick={onCreate}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          <PlusCircle size={14} />
          {t("Create new snippet")}
        </button>
      </div>
    </div>
  );
};

const SnippetRow = ({
  snippet: s,
  onEdit,
  confirmId,
  setConfirmId,
  deleteMut,
}: {
  snippet: EmailSnippetView;
  onEdit: (s: EmailSnippetView) => void;
  confirmId: string | null;
  setConfirmId: (id: string | null) => void;
  deleteMut: any;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer group"
      onClick={() => onEdit(s)}
    >
      {s.visibility === "Private" ? (
        <Lock size={13} className="text-gray-400 shrink-0" />
      ) : (
        <Globe size={13} className="text-blue-400 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 truncate">{s.name || "Untitled"}</div>
        {s.subject && <div className="text-xs text-gray-400 truncate">{s.subject}</div>}
      </div>
      {s.tags && s.tags.length > 0 && (
        <span className="text-xs text-gray-400 shrink-0 px-2">{s.tags.join(", ")}</span>
      )}
      {/* Delete */}
      {confirmId === s.id ? (
        <div className="flex items-center gap-1 shrink-0 px-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => deleteMut.mutate(s.id)}
            disabled={deleteMut.isPending}
            className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1"
          >
            {t("Confirm")}
          </button>
          <button
            onClick={() => setConfirmId(null)}
            className="text-xs text-gray-400 hover:text-gray-600 ml-1 p-1"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setConfirmId(s.id);
          }}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity shrink-0 p-1"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

// ─── Create / Edit Form ───────────────────────────────────────────────────────

export const SnippetForm = ({
  workspaceId,
  initial,
  prefill,
  onSaved,
  onCancel,
}: {
  workspaceId: string;
  initial?: EmailSnippetView;
  prefill?: { subject?: string; description?: string };
  onSaved: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState(initial?.name ?? "");
  const [subject, setSubject] = useState(initial?.subject ?? prefill?.subject ?? "");
  const [description, setDescription] = useState(
    initial?.description ?? prefill?.description ?? ""
  );
  const [visibility, setVisibility] = useState<"Private" | "Shared">(
    initial?.visibility ?? "Private"
  );
  const [projectId, setProjectId] = useState(initial?.projectId ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [pipelineError, setPipelineError] = useState(false);

  const toolbarRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);

  const attributes = useSelector((state: any) => state.template.get("attributes"));
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));

  const { data: projects = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["projects-map"],
    queryFn: () =>
      getProjects().then((list: any[]) => list.map((p) => ({ id: p.id, name: p.name }))),
    staleTime: 60_000 * 10,
  });

  const saveMut = useMutation({
    mutationFn: (form: EmailSnippetForm) => saveEmailSnippet(workspaceId, form),
    onSuccess: onSaved,
  });

  const addTag = (text: string) => {
    const trimmed = text.trim();
    if (trimmed && !tags.includes(trimmed)) setTags([...tags, trimmed]);
    setTagInput("");
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (visibility === "Shared" && !projectId) {
      setPipelineError(true);
      return;
    }
    setPipelineError(false);
    saveMut.mutate({
      id: initial?.id,
      name: name.trim(),
      subject: subject.trim(),
      description: description.trim(),
      visibility,
      projectId: visibility === "Shared" ? projectId : undefined,
      tags,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      {/* Row: Name */}
      <div className="flex items-center border-b border-gray-100 py-3">
        <span className="w-24 shrink-0 text-sm text-gray-400">
          {t("Name")} <span className="text-red-400">*</span>
        </span>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("Snippet name...")}
          className="flex-1 text-sm text-gray-800 placeholder-gray-300 outline-none bg-transparent"
        />
      </div>

      {/* Row: Subject */}
      <div className="flex items-center border-b border-gray-100 py-3">
        <span className="w-24 shrink-0 text-sm text-gray-400">{t("Subject")}</span>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t("Email subject...")}
          className="flex-1 text-sm text-gray-800 placeholder-gray-300 outline-none bg-transparent"
        />
      </div>

      {/* Row: Visibility */}
      <div className="flex items-start border-b border-gray-100 py-3">
        <span className="w-24 shrink-0 text-sm text-gray-400 pt-0.5">{t("Visibility")}</span>
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex gap-4">
            {(["Private", "Shared"] as const).map((v) => (
              <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  checked={visibility === v}
                  onChange={() => {
                    setVisibility(v);
                    if (v === "Private") {
                      setProjectId("");
                      setPipelineError(false);
                    }
                  }}
                  className="accent-blue-600 w-3.5 h-3.5"
                />
                <span className="text-sm text-gray-700">{t(v)}</span>
              </label>
            ))}
          </div>

          {/* Pipeline selector — only when Shared */}
          {visibility === "Shared" && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-xs text-gray-400">{t("Pipeline")}</span>
                <span className="text-xs text-red-400">*</span>
              </div>
              <select
                required
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setPipelineError(false);
                }}
                className={`w-full text-sm outline-none bg-transparent border rounded-md px-2.5 py-1.5 transition-colors ${
                  pipelineError
                    ? "border-red-400 text-red-500 bg-red-50"
                    : projectId
                      ? "border-gray-200 text-gray-800"
                      : "border-gray-200 text-gray-400"
                }`}
              >
                <option value="">{t("Select pipeline...")}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {pipelineError && (
                <span className="text-xs text-red-500 flex items-center gap-1">
                  <span>⚠</span>
                  {t("Pipeline is required for shared snippets.")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row: Tags */}
      <div className="flex items-center border-b border-gray-100 py-3">
        <span className="w-24 shrink-0 text-sm text-gray-400">{t("Tags")}</span>
        <div className="flex-1 flex flex-wrap gap-1.5 items-center">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:text-blue-800 transition-colors focus:outline-none"
              >
                ×
              </button>
            </span>
          ))}
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            onBlur={() => {
              if (tagInput.trim()) addTag(tagInput);
            }}
            placeholder={tags.length === 0 ? t("Add tags... (Enter to confirm)") : ""}
            className="flex-1 min-w-[140px] text-sm text-gray-800 placeholder-gray-300 outline-none bg-transparent"
          />
        </div>
      </div>

      {/* Row: Content */}
      <div className="flex flex-col border-b border-gray-100 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">{t("Content")}</span>
        </div>
        <div className="max-w-full rounded-lg border border-gray-300 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!padding-0 [&_.ql-container]:!border-0 [&_.ql-editor]:min-h-[160px] [&_.ql-editor]:max-h-[300px] [&_.ql-toolbar_button:not([class*='ql-'])]:w-auto!">
          {/* Custom toolbar */}
          <div
            ref={toolbarRef}
            className="flex items-center flex-wrap gap-1 px-2 py-1.5 border-b border-gray-200"
          >
            <EmailVariablePicker quillRef={editorRef} />
            {/* Divider */}
            <div className="w-px h-4 bg-gray-300 mx-0.5" />
            {/* Quill format buttons */}
            <span className="ql-formats">
              <button className="ql-bold" />
              <button className="ql-italic" />
              <button className="ql-underline" />
            </span>
            <span className="ql-formats">
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
            </span>
            <span className="ql-formats">
              <button className="ql-indent" value="-1" />
              <button className="ql-indent" value="+1" />
              <button className="ql-link" />
            </span>
          </div>
          <Editor
            editorInputRef={editorRef}
            toolbarContainer={toolbarRef}
            showToolbar={true}
            placeholder={t("Email body... type @ to mention, {{ to show smart values")}
            members={members}
            attributes={attributes}
            editorContent={{ htmlContent: description, contents: {} }}
            onChange={(content) => setDescription(content.htmlContent)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ChevronLeft size={14} />
          {t("Back")}
        </button>
        <button
          type="submit"
          disabled={!name.trim() || saveMut.isPending}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {saveMut.isPending && <Loader2 size={13} className="animate-spin" />}
          {initial ? t("Save changes") : t("Create snippet")}
        </button>
      </div>
    </form>
  );
};

export default SnippetManagerModal;
